import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/lib/CartContext";
import { Minus, Plus, Trash, Truck, ShoppingCart, ShieldCheck, Lock, ArrowLeft } from "@phosphor-icons/react";

const LOGO_IMG = "/assets/hero-logo.png";
const fmt = (n) => `$${n.toFixed(2)}`;

export default function Cart() {
  const { detailedItems, subtotal, updateQty, removeItem, loading, productsList } = useCart();
  const navigate = useNavigate();

  const shipping = subtotal > 0 ? 0 : 0; // free shipping
  const total = subtotal + shipping;

  return (
    <div className="min-h-screen bg-[#080412] text-white font-sans" data-testid="cart-page">
      <header className="border-b border-white/8 bg-[#080412]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="cart-brand-logo">
            <img src={LOGO_IMG} alt="House Rules" className="h-12 md:h-14 w-auto rounded-lg drop-shadow-[0_0_20px_rgba(167,66,255,0.5)]" />
          </Link>
          <Link
            to="/"
            className="font-sora text-[10.5px] font-semibold uppercase tracking-[0.24em] flex items-center gap-2 text-white/80 hover:text-brandpink transition-colors"
            data-testid="cart-continue-shopping"
          >
            <ArrowLeft size={14} weight="bold" /> Keep Shopping
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-10 md:py-16">
        <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.32em] text-brandpink mb-3">Your cart</div>
        <h1 className="font-display uppercase text-5xl sm:text-6xl md:text-7xl leading-[0.9] tracking-[-0.01em] mb-10" data-testid="cart-headline">
          Cart, <span className="italic font-editorial text-brandpink hr-text-glow-pink">let&apos;s ship it.</span>
        </h1>

        {loading ? (
          <p className="font-sans text-lg text-white/70">Loading…</p>
        ) : detailedItems.length === 0 ? (
          <EmptyCart productsList={productsList} />
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4">
              {detailedItems.map((item) => (
                <div
                  key={item.product_id}
                  className="flex gap-4 sm:gap-6 p-4 sm:p-6 rounded-2xl hr-glass"
                  data-testid="cart-item"
                >
                  <img
                    src={item.product.image}
                    alt={item.product.name}
                    className="w-24 h-24 sm:w-32 sm:h-32 object-cover rounded-xl border border-white/10"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-display uppercase text-2xl sm:text-3xl leading-tight text-white">{item.product.name}</div>
                    <p className="font-sans text-sm text-white/60 mt-1 line-clamp-2">{item.product.tagline}</p>
                    <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center border border-white/15 rounded-full overflow-hidden bg-white/[0.03]">
                        <button
                          aria-label="Decrease quantity"
                          onClick={() => updateQty(item.product_id, item.quantity - 1)}
                          className="px-3 py-2 hover:bg-white/8 text-white"
                          data-testid="cart-decrease-qty"
                        >
                          <Minus size={16} weight="bold" />
                        </button>
                        <span className="px-4 font-mono text-sm text-white" data-testid="cart-item-qty">{item.quantity}</span>
                        <button
                          aria-label="Increase quantity"
                          onClick={() => updateQty(item.product_id, item.quantity + 1)}
                          className="px-3 py-2 hover:bg-white/8 text-white"
                          data-testid="cart-increase-qty"
                        >
                          <Plus size={16} weight="bold" />
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(item.product_id)}
                        className="font-sora text-[10px] font-semibold uppercase tracking-[0.24em] text-white/50 hover:text-brandpink flex items-center gap-2 transition-colors"
                        data-testid="cart-remove-item"
                      >
                        <Trash size={14} weight="duotone" /> Remove
                      </button>
                      <div className="font-anton text-2xl text-white ml-auto" data-testid="cart-line-total">
                        {fmt(item.lineTotal)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              <div className="pt-4 flex items-center gap-3 flex-wrap">
                <ShieldCheck size={18} weight="duotone" className="text-brandpink" />                <span className="font-sans text-sm text-white/60">
                  30-day money-back guarantee · Free US shipping · Secure checkout by Stripe
                </span>
              </div>
            </div>

            <aside className="lg:col-span-1">
              <div className="rounded-2xl hr-glass hr-neon-pink p-6 sticky top-24" data-testid="cart-summary">
                <div className="font-display uppercase text-3xl mb-5 text-white">Order summary</div>
                <Row label="Subtotal" value={fmt(subtotal)} testid="cart-subtotal" />
                <Row label="Shipping" value="FREE" muted />
                <Row label="Taxes" value="Calculated at checkout" muted small />
                <div className="border-t border-white/12 my-4" />
                <Row label="Total" value={fmt(total)} bold testid="cart-total" />

                <button
                  onClick={() => navigate("/checkout")}
                  className="hr-cta w-full mt-6 h-13 py-4 rounded-full text-white font-sora font-semibold text-sm uppercase tracking-[0.22em] flex items-center justify-center gap-2"
                  data-testid="cart-checkout-button"
                >
                  <Lock size={16} weight="duotone" /> Secure Checkout
                </button>

                <div className="mt-4 flex items-center gap-2 text-xs text-white/50 font-sans">
                  <Truck size={14} weight="duotone" /> Ships in 3–5 business days
                </div>
              </div>
            </aside>
          </div>
        )}
      </main>
    </div>
  );
}

const Row = ({ label, value, bold, muted, small, testid }) => (
  <div className={`flex items-center justify-between py-1 ${small ? "text-sm" : ""}`}>
    <span className={`font-sans ${muted ? "text-white/50" : "text-white/85"}`}>{label}</span>
    <span
      className={`${bold ? "font-anton text-2xl text-white" : "font-sora font-semibold text-white"} ${muted ? "text-white/50" : ""}`}
      data-testid={testid}
    >
      {value}
    </span>
  </div>
);

const EmptyCart = ({ productsList }) => {
  const { addItem } = useCart();
  return (
    <div className="rounded-2xl border border-dashed border-white/20 p-10 text-center hr-glass">
      <div className="font-display uppercase text-4xl mb-2 text-white">Your cart is empty.</div>
      <p className="font-sans text-white/60 mb-8">Add a House Rules box to get the party started.</p>
      <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
        {productsList.map((p) => (
          <div key={p.id} className="rounded-2xl p-4 hr-glass text-left">
            <img src={p.image} alt={p.name} className="w-full h-40 object-cover rounded-xl border border-white/10 mb-3" />
            <div className="font-display uppercase text-xl text-white">{p.name}</div>
            <div className="font-anton text-2xl text-brandpink mb-3">${p.price.toFixed(2)}</div>
            <button
              onClick={() => addItem(p.id, 1)}
              className="hr-cta w-full h-11 rounded-full text-white font-sora font-semibold text-[10.5px] uppercase tracking-[0.22em] flex items-center justify-center gap-2"
              data-testid="empty-cart-add-button"
            >
              <ShoppingCart size={14} weight="fill" /> Add to Cart
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
